/**
 * PaymentPageV2 - Main entry point for the new payment page
 *
 * Features:
 * - Wraps with Stripe Elements provider
 * - Two-column responsive layout (desktop)
 * - Single column with correct ordering (mobile)
 * - Sticky CTA button on mobile only
 */

import '../dashboard/dashboard-reset.css';
import { Elements } from '@stripe/react-stripe-js';
import { useSearchParams } from 'react-router-dom';
import usePaymentController from '@/app/components/payment/payment.controller';
import useCollectPaymentMethod from '@/app/components/payment/components/method/payment-method-v2.controller';
import useOnMount from '@/hooks/useOnMount';
import { useSetIntercomIsVisible } from '@/queries/layout.queries';
import {
  StripeAmountProvider,
  useStripeAmount,
} from '@/app/components/ppra/components/ppra-payment-page/contexts/StripeAmountContext';
import { useStripeAmountUpdater } from '@/app/components/ppra/components/ppra-payment-page/hooks/useStripeAmountUpdater';
import { VARS } from '@/app/components/ppra/components/common/constants';

import { PaymentPageLayout } from './PaymentPageLayout';
import { Header } from './components/Header';
import { HeroSection } from './components/HeroSection';
import { FeaturesList } from './components/left-column/FeaturesList';
import { TestimonialsSection } from './components/left-column/TestimonialsSection';
import { PrivacySection } from './components/left-column/PrivacySection';
import { FAQSection } from './components/FAQSection';
import { PaymentCard } from './components/right-column/PaymentCard';
import { FooterLinks } from './components/FooterLinks';
import { FooterDisclaimer } from './components/FooterDisclaimer';
import { ContactButton } from './components/ContactButton';
import { StickyMobileCTA } from './components/StickyMobileCTA';

import {
  PricingHeader,
  EmailField,
  NameField,
  CardSection,
  ExpressCheckoutSection,
  ZipCodeField,
  CouponInput,
  SubmitButton,
  TrialBenefits,
  PaymentTrustBadges,
  LegalDisclaimer,
} from '@/components/payment-form';
import { useForm } from 'react-hook-form';
import useVisitorSession from '@/hooks/useVisitorSession';
import authService from '@/services/auth.service';
import { useCallback, useEffect, useRef, useState } from 'react';

interface PaymentFormValues {
  email: string;
  name: string;
  zip: string;
  coupon: string;
}

/**
 * PaymentForm - The actual payment form with all fields
 */
function PaymentForm() {
  useStripeAmountUpdater();

  const {
    coupon,
    setCoupon,
    couponValidationError,
    isSubmitting,
    getPrice,
    handleSubmit,
    handleExpressCheckout,
    isTrial,
    isPriceLoading,
    priceRecurring,
    email: sessionEmail,
  } = useCollectPaymentMethod();

  const { captureLeadAndName } = useVisitorSession();
  const hasLeadCapturedRef = useRef(false);

  const form = useForm<PaymentFormValues>({
    defaultValues: {
      email: '',
      name: '',
      zip: '',
      coupon: '',
    },
  });

  // Sync email from session when it becomes available
  useEffect(() => {
    if (sessionEmail && !form.getValues('email')) {
      form.setValue('email', sessionEmail);
      hasLeadCapturedRef.current = true;
    }
  }, [sessionEmail, form]);

  // Prefill name from local storage on mount
  useOnMount(() => {
    const firstName = authService.getVisitorFirstName();
    if (firstName) {
      form.setValue('name', firstName);
    }
  });

  // Capture lead when email field is blurred with valid email
  const handleLeadCapture = useCallback(
    async (email: string) => {
      if (hasLeadCapturedRef.current) return;
      hasLeadCapturedRef.current = true;

      const fullName = form.getValues('name');
      try {
        await captureLeadAndName({
          email,
          name: fullName || undefined, // Full name → UserProfile.name in DB
        });
      } catch (error) {
        // Reset flag so they can try again
        hasLeadCapturedRef.current = false;
        console.error('Failed to capture lead:', error);
      }
    },
    [captureLeadAndName, form]
  );

  // Capture name when name field is blurred (updates lead if already captured)
  const handleNameCapture = useCallback(
    async (fullName: string) => {
      // If lead already captured, send name update to backend
      if (hasLeadCapturedRef.current) {
        const email = form.getValues('email');
        if (email) {
          try {
            await captureLeadAndName({
              email,
              name: fullName, // Full name → UserProfile.name in DB
            });
          } catch (error) {
            console.error('Failed to update name:', error);
          }
        }
      }
      // If lead not yet captured, name will be included when email is captured
    },
    [captureLeadAndName, form]
  );

  const annualPrice =
    typeof priceRecurring?.amount === 'number'
      ? Math.round(priceRecurring.amount / 100)
      : 119;

  // Check if form is valid for sticky CTA
  const isFormValid = form.formState.isValid;

  // Track visibility of main submit button to show/hide sticky CTA
  const submitButtonRef = useRef<HTMLDivElement>(null);
  const [isSubmitButtonVisible, setIsSubmitButtonVisible] = useState(true);

  useEffect(() => {
    const checkSubmitButtonVisibility = () => {
      if (submitButtonRef.current) {
        const rect = submitButtonRef.current.getBoundingClientRect();
        // Button is visible if its bottom is above the viewport top
        setIsSubmitButtonVisible(rect.bottom > 0);
      }
    };

    window.addEventListener('scroll', checkSubmitButtonVisibility);
    window.addEventListener('resize', checkSubmitButtonVisibility);
    checkSubmitButtonVisibility();

    return () => {
      window.removeEventListener('scroll', checkSubmitButtonVisibility);
      window.removeEventListener('resize', checkSubmitButtonVisibility);
    };
  }, []);

  // Handle sticky CTA click
  // If form is valid, submit payment; otherwise scroll to payment card
  const handleStickyCTAClick = () => {
    if (isFormValid) {
      handleSubmit();
    } else {
      const paymentCard = document.getElementById('payment-card');
      if (paymentCard) {
        paymentCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <PaymentPageLayout
      header={<Header />}
      hero={<HeroSection isTrial={isTrial} />}
      paymentCard={
        <PaymentCard id="payment-card" isSticky={false}>
          {/* Pricing */}
          <PricingHeader
            isTrial={isTrial}
            annualPrice={annualPrice}
            isLoading={isPriceLoading}
          />

          {/* Form Fields */}
          <form onSubmit={form.handleSubmit(() => handleSubmit())}>
            <div className="flex flex-col gap-4">
              <EmailField
                registration={form.register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                error={form.formState.errors.email?.message}
                onLeadCapture={handleLeadCapture}
              />

              <NameField
                registration={form.register('name', {
                  required: 'Name is required',
                })}
                error={form.formState.errors.name?.message}
                onNameCapture={handleNameCapture}
              />

              <CardSection
                email={form.watch('email')}
                name={form.watch('name')}
              />

              <ZipCodeField
                registration={form.register('zip', {
                  required: 'Zip code is required',
                  pattern: {
                    value: /^\d{5}$/,
                    message: 'Enter a valid 5-digit zip code',
                  },
                })}
                error={form.formState.errors.zip?.message}
              />

              {/* TODO: Re-enable coupon input once we have proper trial discount UI
              <CouponInput
                value={coupon}
                onChange={setCoupon}
                onValidate={() => getPrice()}
                error={couponValidationError || undefined}
              />
              */}
            </div>

            {/* Express Checkout - positioned after form fields per Figma */}
            <div className="mt-4">
              <ExpressCheckoutSection onConfirm={handleExpressCheckout} />
            </div>

            {/* Submit */}
            <div ref={submitButtonRef} className="mt-6 flex flex-col gap-2">
              <SubmitButton isTrial={isTrial} isSubmitting={isSubmitting} />
              <LegalDisclaimer />
            </div>
          </form>

          {/* Benefits & Trust */}
          <TrialBenefits isTrial={isTrial} />
          <PaymentTrustBadges />
        </PaymentCard>
      }
      featuresList={<FeaturesList />}
      testimonials={<TestimonialsSection />}
      privacy={<PrivacySection />}
      faqs={
        <div id="faqs">
          <FAQSection />
        </div>
      }
      contactButton={<ContactButton />}
      footer={
        <div className="flex flex-col gap-6">
          <FooterLinks />
          <FooterDisclaimer />
        </div>
      }
      stickyMobileCTA={
        <StickyMobileCTA
          isEnabled={isFormValid}
          onClick={handleStickyCTAClick}
          isSubmitting={isSubmitting}
          isVisible={!isSubmitButtonVisible}
        >
          Start my risk assessment
        </StickyMobileCTA>
      }
    />
  );
}

/**
 * Wrapper that provides Stripe Elements context
 */
function PaymentPageWithElements({ children }: { children: React.ReactNode }) {
  const { amount } = useStripeAmount();
  const [searchParams] = useSearchParams();
  const isTrial = searchParams.get('trial') !== 'false';

  const { stripePromise, stripeOptions } = usePaymentController({
    variant: 'seamless',
    amount: amount || VARS.subscription.defaultAmount,
    isTrial,
  });

  return (
    <Elements options={stripeOptions} stripe={stripePromise}>
      {children}
    </Elements>
  );
}

/**
 * Main exported component
 */
export function PaymentPageV2() {
  const setIntercomIsVisible = useSetIntercomIsVisible();

  useOnMount(() => {
    setIntercomIsVisible.mutate(false);
    return () => {
      setIntercomIsVisible.mutate(true);
    };
  });

  return (
    <StripeAmountProvider defaultAmount={VARS.subscription.defaultAmount}>
      <PaymentPageWithElements>
        <PaymentForm />
      </PaymentPageWithElements>
    </StripeAmountProvider>
  );
}

export default PaymentPageV2;
